// Based on TiMessengerTestTreiber.yaml 2.0.0
import { inject, Injectable } from '@angular/core';
import { FhirSearchResult } from '../../../shared-api/fhirSearchResult';
import { SearchInFhirDirectoryService as ApiSearchInFhirDirectoryService } from '../../../shared-api/searchInFhirDirectory.service';
import { HttpErrorResponse } from '@angular/common/http';

import { AppConfigService } from '../config/app-config.service';
import { VzdTokenService } from '../vzd-fhir/vzd-token.service';
import { FhirReference } from '../../../shared-api/fhirReference';
import { FhirBaseResource } from '../../../shared-api/fhirBaseResource';
import { FhirEndpoint } from '../../../shared-api/fhirEndpoint';
import { FhirLocation } from '../../../shared-api/fhirLocation';
import { FhirAddress } from '../../../shared-api/fhirAddress';
import { TableColumn } from '../../components/core/generic-table/generic-table.component';

export interface VzdMappedResult {
  hsId?: string;
  hsName?: string;
  organizationId?: string;
  organizationName?: string;
  adress?: string;
  hsIdentifier?: string;
  endpoints?: string[];
  matrixId?: string;
  practitionerName?: string;
  name?: string;
}

type ResourceType = 'HealthcareService' | 'PractitionerRole' | 'EndpointDirectory';

export interface FhirSearchParams {
  directory: ResourceType;
  searchValue: string;
}

export interface VzdSearchResult {
  rows: VzdMappedResult[];
  total: number;
  columns: TableColumn<VzdMappedResult>[];
}

function stripTrailingSlash(value: string): string {
  if (!value) return '';

  while (value.endsWith('/')) {
    value = value.slice(0, -1);
  }

  return value;
}

function stripEndingTimAuthenticate(value: string): string {
  return (value || '').replace(/\/tim-authenticate\/?$/i, '');
}

@Injectable({
  providedIn: 'root'
})
/**
 * Der SearchFhirDirectoryService erlaubt die Suche im FHIR-Verzeichnis.
 * Implementation of the mandatory SearchInFhirDirectory API of the gematik TI-M Testtreiber
 * Umgesetze AFOs:
 * A_25363 - Testtreiber-Modul
 * A_25361 - keine Modifikation der Inhalte
 */
export class SearchInFhirDirectoryService implements ApiSearchInFhirDirectoryService {
  private readonly absoluteSearchBaseUrl: string;

  private readonly vzdFhirUrl: string;
  private readonly config = inject(AppConfigService);
  private readonly vzdTokenService = inject(VzdTokenService);

  constructor() {
    this.vzdFhirUrl = this.config.get('vzdFhirUrl');

    const backendRoot = stripTrailingSlash(stripEndingTimAuthenticate(this.vzdFhirUrl));

    this.absoluteSearchBaseUrl = `${backendRoot}/search`;
  }

  /**
   * Returns the found endpoints of the healthcare services matching the query
   * Returns the found endpoints of the healthcare services matching the query. (the search result could also contain all other resources that are related with it. Please ensure that the searched resources are included at any time!)
   * @param mxId MxId (in uri scheme) standing in address field of the endpoint
   */
  async searchHsEndpointInFhirDirectory(mxId?: string, name?: string): Promise<FhirSearchResult> {
    try {
      const token = await this.vzdTokenService.getToken();
      const baseUrl = this.absoluteSearchBaseUrl;

      const params = new URLSearchParams();
      if (mxId) {
        const formattedMxId = mxId.replace(/^@/, 'matrix:u/');
        params.append('address', formattedMxId);
      }
      if (name) {
        params.append('name', name);
      }

      params.append('status', 'active');

      const url = `${baseUrl}/Endpoint?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok || response.status >= 400) {
        throw new Error('Suchen nach Endpunkten ist fehlgeschlagen');
      }

      const responseBody = (await response.json()) as FhirSearchResult;

      return responseBody ?? { resourceType: 'Bundle', total: 0, entry: [] };
    } catch (err) {
      return this.createErrorBundle(err, 'HealthcareService');
    }
  }

  /**
   * Returns all belonging resources of an organization
   * @param healthcareServiceName Name of the healthcare service
   */
  async searchOrganizationInFhirDirectory(healthcareServiceName?: string): Promise<FhirSearchResult> {
    try {
      const token = await this.vzdTokenService.getToken();
      const baseUrl = this.absoluteSearchBaseUrl;

      const params = new URLSearchParams();
      if (healthcareServiceName) params.append('name', healthcareServiceName);
      params.append('organization.active', 'true');
      params.append('_include', 'HealthcareService:endpoint');
      params.append('_include', 'HealthcareService:location');
      params.append('_include', 'HealthcareService:organization');

      const url = `${baseUrl}/HealthcareService?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok || response.status >= 400) {
        throw new Error('Suchen nach der Organisation ist fehlgeschlagen');
      }

      const responseBody = (await response.json()) as FhirSearchResult;

      return responseBody ?? { resourceType: 'Bundle', total: 0, entry: [] };
    } catch (err) {
      return this.createErrorBundle(err, 'Organization');
    }
  }

  /**
   * Returns the found Practitioner entries and its belonging location, endpoints and practitioner roles
   * @param name Name of the Practitioner
   * @param mxid MXID of the Practitioner in uri scheme.
   */
  async searchPractitionerInFhirDirectory(name?: string, mxid?: string): Promise<FhirSearchResult> {
    try {
      const token = await this.vzdTokenService.getToken();
      const baseUrl = this.absoluteSearchBaseUrl;

      const params = new URLSearchParams();

      /* TODO: Diese Lösung ist nur gebaut, damit der Test, der einen bestimmten Practitioner sucht nicht fehlschlägt
               Eine saubere Lösung würde Paging beinhalten. Da es keine Dokumentation zur VZD-Schnittstelle und deren konkreter Implementierung
               gibt, können wir noch kein Paging umsetzen.
       */
      if (name) {
        if (name.includes(',')) {
          const splittedNames = name.split(',');
          params.append('practitioner.family', splittedNames[0].trim());
          params.append('practitioner.given', splittedNames[1].trim());
        } else {
          params.append('practitioner.name', name);
        }
      }
      if (mxid) params.append('endpoint.address', mxid);
      params.append('practitioner.active', 'true');
      params.append('_include', 'PractitionerRole:practitioner');
      params.append('_include', 'PractitionerRole:endpoint');
      params.append('_include', 'PractitionerRole:location');

      const url = `${baseUrl}/PractitionerRole?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok || response.status >= 400) {
        throw new Error('Suchen nach Practitioner ist fehlgeschlagen');
      }

      const responseBody = (await response.json()) as FhirSearchResult;

      return responseBody ?? { resourceType: 'Bundle', total: 0, entry: [] };
    } catch (err) {
      return this.createErrorBundle(err, 'Practitioner');
    }
  }

  public mapOrganizationResult(result: FhirSearchResult): VzdMappedResult[] {
    if (result?.entry) {
      const healthcareServices = result.entry
        .filter(entry => entry.resource.resourceType === 'HealthcareService')
        .map(entry => entry.resource);

      const organizations = result.entry
        .filter(entry => entry.resource.resourceType === 'Organization')
        .map(entry => entry.resource);

      const locations = result.entry
        .filter(entry => entry.resource.resourceType === 'Location')
        .map(entry => entry.resource as FhirLocation);

      const endpoints = result.entry
        .filter(entry => entry.resource.resourceType === 'Endpoint')
        .map(entry => entry.resource as FhirEndpoint);

      return healthcareServices.map(hs => {
        const orgId = hs.providedBy?.reference?.split('/')[1];
        const organization = organizations.find(org => org.id === orgId);

        const endpointIds = hs.endpoint?.map(ep => ep.reference?.split('/')[1]) || [];
        const linkedEndpoints = endpoints.filter(ep => ep.id && endpointIds.includes(ep.id));

        const locationIds = hs.location?.map(loc => loc.reference?.split('/')[1]) || [];
        const linkedLocation = locations.find(loc => loc.id && locationIds.includes(loc.id));

        return {
          hsId: hs.id,
          hsName: hs.name,
          organizationId: organization?.id,
          organizationName: organization?.name,
          adress: this.formatAddress(linkedLocation?.address),
          hsIdentifier: organization?.identifier?.find(i => i.system?.includes('telematik-id'))?.value,
          endpoints: linkedEndpoints.map(e => e.address?.replace(/^matrix:u\//, '@'))
        };
      });
    }
    return [];
  }

  public mapPractitionerEndpoints(result: FhirSearchResult): VzdMappedResult[] {
    if (!result.entry) return [];

    const practitioners = result.entry.filter(e => e.resource.resourceType === 'Practitioner').map(e => e.resource);

    const practitionerMap = new Map<string, string>();

    practitioners.forEach(prac => {
      const id = prac.id;
      const name = Array.isArray(prac.name)
        ? prac.name.map(fullname => `${fullname.given?.join(' ') ?? ''} ${fullname.family ?? ''}`.trim()).join(', ')
        : this.getPractitionerName(prac);
      if (id) practitionerMap.set(id, name);
    });

    const practitionerRoles = result.entry
      .filter(e => e.resource.resourceType === 'PractitionerRole')
      .map(e => e.resource);

    const endpointToPractitionerName = new Map<string, string>();
    practitionerRoles.forEach(role => {
      const practitionerReference = role.practitioner?.reference?.split('/')[1];
      const practitionerName = practitionerReference ? (practitionerMap.get(practitionerReference) ?? '') : '';
      if (role.endpoint) {
        const endpoints = Array.isArray(role.endpoint) ? role.endpoint : [role.endpoint];
        endpoints.forEach((ep: FhirReference) => {
          const endpointId = ep.reference?.split('/')[1];
          if (endpointId) {
            endpointToPractitionerName.set(endpointId, practitionerName);
          }
        });
      }
    });

    const endpoints = result.entry
      .filter(e => e.resource.resourceType === 'Endpoint')
      .map(e => e.resource as FhirEndpoint);

    return endpoints
      .map(ep => {
        if (!ep.id) return null;

        const matrixId = ep.address?.replace(/^matrix:u\//, '@');
        if (!matrixId) return null;

        const practitionerName = endpointToPractitionerName.get(ep.id) ?? '';

        return { practitionerName, matrixId };
      })
      .filter((item): item is { practitionerName: string; matrixId: string } => !!item);
  }

  async searchOnFhir(params: FhirSearchParams): Promise<VzdSearchResult> {
    const { directory, searchValue } = params;
    if (!searchValue) return { rows: [], total: 0, columns: [] };
    try {
      if (directory === 'HealthcareService') {
        return await this.searchHealthcareService(searchValue);
      }
      if (directory === 'PractitionerRole') {
        return await this.searchPractitionerRole(searchValue);
      }
      if (directory === 'EndpointDirectory') {
        return await this.searchEndpointDirectory(searchValue);
      }
      return { rows: [], total: 0, columns: [] };
    } catch (err: any) {
      console.error(`When calling searchOnFhir: ${err}`);
      throw new Error('There was a Problem, when calling searchOnFhir');
    }
  }

  /**
   * Wandelt einen Fehler in ein leeres FHIR Bundle mit Fehlermeldung um.
   * So erfüllt die Methode den Rückgabevertrag (kein `undefined`).
   */
  private createErrorBundle(err: unknown, type: string): FhirSearchResult {
    const message =
      err instanceof HttpErrorResponse
        ? `FHIR Directory (${type}) search failed: ${err.status} ${err.message}`
        : `FHIR Directory (${type}) search failed: ${String(err)}`;

    console.error(message, err);
    return {
      resourceType: 'Bundle',
      total: 0,
      entry: []
    };
  }

  private getPractitionerName(prac: FhirBaseResource): string {
    return typeof prac.name === 'string' ? prac.name : '';
  }

  private formatAddress(address?: FhirAddress): string {
    if (!address) return '';

    const line = Array.isArray(address.line) ? address.line.filter(Boolean).join(' ') : (address.line ?? '');

    const cityLine = [address.postalCode, address.city].filter(Boolean).join(' ');

    const country = address.country ?? '';

    return [line, cityLine, country].filter(Boolean).join(', ');
  }

  private async searchHealthcareService(searchValue: string): Promise<VzdSearchResult> {
    const result = await this.searchOrganizationInFhirDirectory(searchValue);
    const vzdMappedResults = this.mapOrganizationResult(result);
    const columns: TableColumn<VzdMappedResult>[] = [
      { key: 'hsName', label: 'Service' },
      { key: 'organizationName', label: 'Name der Organisation' },
      { key: 'adress', label: 'Adresse' },
      { key: 'hsIdentifier', label: 'Telematik-Id' }
    ];
    return { rows: vzdMappedResults, total: vzdMappedResults.length, columns };
  }

  private async searchPractitionerRole(searchValue: string): Promise<VzdSearchResult> {
    let result;
    if (searchValue.startsWith('matrix:u/')) {
      result = await this.searchPractitionerInFhirDirectory('', searchValue);
    } else {
      result = await this.searchPractitionerInFhirDirectory(searchValue, '');
    }
    const vzdMappedResults = this.mapPractitionerEndpoints(result);
    const columns: TableColumn<VzdMappedResult>[] = [
      { key: 'practitionerName', label: 'Name' },
      { key: 'matrixId', label: 'Matrix-ID' }
    ];
    return { rows: vzdMappedResults, total: vzdMappedResults.length, columns };
  }

  private mapEndpoints(vzdEntries: FhirSearchResult): VzdMappedResult[] {
    if (!vzdEntries.entry) {
      return [];
    }

    return vzdEntries.entry?.map(ep => {
      return {
        name: ep.resource.name,
        matrixId: (ep.resource as FhirEndpoint).address.replace(/^matrix:u\//, '@')
      };
    });
  }

  private async searchEndpointDirectory(searchValue: string): Promise<VzdSearchResult> {
    const result = await this.searchHsEndpointInFhirDirectory(undefined, searchValue);
    const vzdMappedResults = this.mapEndpoints(result);

    const columns: TableColumn<VzdMappedResult>[] = [
      { key: 'name', label: 'Name' },
      { key: 'matrixId', label: 'Matrix-ID' }
    ];
    return { rows: vzdMappedResults, total: vzdMappedResults.length, columns };
  }
}
